import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  GraduationCap,
  LayoutDashboard,
  LibraryBig,
  LogOut,
  Menu,
  Users,
  X,
} from 'lucide-react';
import { UserRole } from '@tutor/shared';
import { Avatar, Button, cn } from '@tutor/ui';
import { useAuthStore } from '../../stores/auth.store';
import { NotificationBell } from './NotificationBell';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
}

const TUTOR_NAV: NavItem[] = [
  { to: '/tutor/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/tutor/calendar', label: 'Calendar', icon: CalendarDays },
  { to: '/tutor/lessons', label: 'Lessons', icon: BookOpen },
  { to: '/tutor/students', label: 'Students', icon: Users },
  { to: '/tutor/homework', label: 'Homework', icon: ClipboardCheck },
  { to: '/tutor/assessments', label: 'Assessments', icon: BarChart3 },
  { to: '/tutor/resources', label: 'Resources', icon: LibraryBig },
];

const STUDENT_NAV: NavItem[] = [
  { to: '/student/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/student/calendar', label: 'Calendar', icon: CalendarDays },
  { to: '/student/lessons', label: 'Lessons', icon: BookOpen },
  { to: '/student/homework', label: 'Homework', icon: ClipboardList },
  { to: '/student/progress', label: 'Progress', icon: BarChart3 },
  { to: '/student/resources', label: 'Resources', icon: LibraryBig },
];

export function AppLayout() {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user) return null;

  const isTutor = user.role === UserRole.TUTOR;
  const nav = isTutor ? TUTOR_NAV : STUDENT_NAV;
  const profileHref = isTutor ? '/tutor/profile' : '/student/profile';

  const handleLogout = async (): Promise<void> => {
    await logout();
    navigate('/login', { replace: true });
  };

  const navigation = (
    <nav className="flex flex-1 flex-col gap-1 px-3">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) => cn('nav-link', isActive && 'nav-link-active')}
        >
          <item.icon className="h-4 w-4 shrink-0" aria-hidden />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="flex h-16 items-center gap-2.5 border-b border-slate-100 px-5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 text-white">
            <GraduationCap className="h-4.5 w-4.5" aria-hidden />
          </span>
          <span className="text-sm font-semibold tracking-tight text-slate-900">Tutor Platform</span>
        </div>

        <div className="flex flex-1 flex-col py-4">
          {navigation}

          <div className="mt-auto px-3 pt-4">
            <NavLink
              to={profileHref}
              className={({ isActive }) => cn('nav-link', isActive && 'nav-link-active')}
            >
              <Avatar name={user.fullName} src={user.avatarUrl} size="xs" />
              <span className="min-w-0 flex-1 truncate">{user.fullName}</span>
            </NavLink>
            <button type="button" onClick={handleLogout} className="nav-link w-full">
              <LogOut className="h-4 w-4 shrink-0" aria-hidden />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="relative flex h-full w-64 flex-col bg-white shadow-xl">
            <div className="flex h-16 items-center justify-between border-b border-slate-100 px-5">
              <span className="text-sm font-semibold text-slate-900">Tutor Platform</span>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </div>
            <div className="flex flex-1 flex-col py-4">
              {navigation}
              <div className="mt-auto px-3">
                <button type="button" onClick={handleLogout} className="nav-link w-full">
                  <LogOut className="h-4 w-4" aria-hidden />
                  Sign out
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-slate-200 bg-white/80 px-4 backdrop-blur sm:px-6">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" aria-hidden />
          </Button>

          <div className="hidden text-sm text-slate-500 lg:block">
            {isTutor ? 'Tutor portal' : 'Student portal'}
          </div>

          <div className="ml-auto flex items-center gap-3">
            <NotificationBell />
            <NavLink to={profileHref} className="flex items-center gap-2.5 rounded-lg px-1 py-1 hover:bg-slate-50">
              <div className="hidden text-right sm:block">
                <p className="text-sm font-medium leading-tight text-slate-900">{user.fullName}</p>
                <p className="text-xs leading-tight text-slate-500">
                  {isTutor ? 'Tutor' : 'Student'}
                </p>
              </div>
              <Avatar name={user.fullName} src={user.avatarUrl} size="sm" />
            </NavLink>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
